import { useEffect, useRef, useState, type CSSProperties } from 'react';
import type { CluePayload } from '@guess/engine';
import { cx } from '../ui/cx';
import type { ClueProps } from './ClueRenderer';
import s from './clue.module.scss';

type TextPayload = Extract<CluePayload, { kind: 'text' }>;

/**
 * L'indice textuel : des mots qui se decouvrent un a un.
 *
 * Le seul vrai travail est de savoir LESQUELS viennent d'apparaitre. Sans ca,
 * chaque palier oblige a relire toute la phrase pour trouver ce qui a change —
 * et a vingt secondes de manche, relire coute la cote d'un palier.
 *
 * On compare donc au rendu precedent et on anime la difference. Le moteur ne
 * nous dit rien de cette difference, et n'a pas a le faire : c'est une
 * question d'affichage.
 */
export function TextClue({ payload, stageToken }: ClueProps<TextPayload>) {
  const previousRef = useRef<Array<string | null>>([]);
  const [fresh, setFresh] = useState<Set<number>>(() => new Set());

  useEffect(() => {
    const previous = previousRef.current;
    const appeared = new Set<number>();
    payload.tokens.forEach((token, index) => {
      // Ce qui etait masque et ne l'est plus. Le premier palier n'anime rien :
      // tout y est nouveau, et tout faire clignoter ne designe rien.
      if (token.text !== null && previous.length > 0 && previous[index] === null) {
        appeared.add(index);
      }
    });
    previousRef.current = payload.tokens.map((t) => t.text);
    setFresh(appeared);
  }, [payload.tokens, stageToken]);

  return (
    <p className={s.text} lang="fr">
      {payload.tokens.map((token, index) => {
        if (token.text === null) {
          return (
            <span
              key={index}
              className={s.blank}
              // La largeur du bloc revele la longueur du mot : c'est une
              // information que le moteur donne exprès (`ClueToken.length`).
              style={{ '--len': token.length } as CSSProperties}
              aria-label="mot masque"
            />
          );
        }
        return (
          <span
            key={index}
            // La cle d'animation inclut le palier : un meme mot qui reapparait
            // a un palier suivant rejoue bien son arrivee.
            className={cx(s.word, fresh.has(index) && s.wordFresh)}
          >
            {token.text}
          </span>
        );
      })}
    </p>
  );
}
